/**
 * The tasks API: the caller's open tasks and the task completion transition.
 *
 * Work order M6 Station 2a item 1.
 * Schema truth: TaskList {total, open, items[TaskOut]} and TaskCompleted {id, completed_at}.
 */

import { apiJson, bool, isRecord, optStr, query, str } from "./client";

export interface TaskNextAction {
  readonly code: string;
  readonly method: string;
  readonly href: string;
  readonly targetType: string | null;
  readonly targetId: string | null;
}

export interface Task {
  readonly id: string;
  readonly kind: string;
  readonly subjectType: string;
  readonly subjectId: string;
  readonly message: string;
  readonly deepLink: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
  /**
   * Wave COM item 1: when the task falls due, from the tenant's task clock (null for a
   * kind with no clock), and when the scheduler marked it overdue (null until it does).
   * The overdue mark is the server's; the client never derives it from the clock.
   */
  readonly dueAt: string | null;
  readonly overdueAt: string | null;
  readonly addressedToParty: boolean;
  /**
   * Wave W1: a workflow task closes only when the act it asks for happens. The client
   * offers "Open the action" for it and never a Complete button. Absent on an older
   * server, which reads as false (the manual path, which the server refuses with 409).
   */
  readonly completesThroughAction: boolean;
  /**
   * Wave Experience A: the task is open and its act waits on the caller. False for an
   * informational task or a completed one; those are "For your information", never a row
   * of the queue. The server's word; an older server's absence reads from the kind flag.
   */
  readonly actionableByMe: boolean;
  readonly nextAction: TaskNextAction;
}

export interface TaskList {
  readonly total: number;
  readonly open: number;
  /** Wave Experience A: the open tasks whose act waits on the caller (the badge's count). */
  readonly actionable?: number;
  readonly items: readonly Task[];
}

export interface TaskCompleted {
  readonly id: string;
  readonly completedAt: string | null;
}

function parseNextAction(raw: unknown): TaskNextAction {
  if (!isRecord(raw)) {
    throw new Error("Expected next action record");
  }
  return {
    code: str(raw.code),
    method: str(raw.method),
    href: str(raw.href),
    targetType: optStr(raw.target_type),
    targetId: optStr(raw.target_id),
  };
}

export function parseTask(raw: Record<string, unknown>): Task {
  return {
    id: str(raw.id),
    kind: str(raw.kind),
    subjectType: str(raw.subject_type),
    subjectId: str(raw.subject_id),
    message: str(raw.message),
    deepLink: optStr(raw.deep_link),
    createdAt: str(raw.created_at),
    completedAt: optStr(raw.completed_at),
    dueAt: optStr(raw.due_at),
    overdueAt: optStr(raw.overdue_at),
    addressedToParty: bool(raw.addressed_to_party),
    completesThroughAction: raw.completes_through_action === true,
    actionableByMe:
      typeof raw.actionable_by_me === "boolean"
        ? raw.actionable_by_me
        : raw.completes_through_action === true && raw.completed_at == null,
    nextAction: parseNextAction(raw.next_action),
  };
}

function parseTaskList(raw: unknown): TaskList {
  if (!isRecord(raw)) {
    throw new Error("Expected task list record");
  }
  const items = Array.isArray(raw.items) ? raw.items : [];
  const parsedItems = items.filter(isRecord).map(parseTask);
  return {
    total: typeof raw.total === "number" ? raw.total : parsedItems.length,
    open: typeof raw.open === "number" ? raw.open : parsedItems.filter(isTaskOpen).length,
    ...(typeof raw.actionable === "number" ? { actionable: raw.actionable } : {}),
    items: parsedItems,
  };
}

/** The record types a task list can be narrowed to (wave W2: the record's own tasks). */
export type TaskRecordType = "PROJECT" | "CONTRACT" | "ITP" | "SCHEDULE" | "FINDING";

export interface TaskRecord {
  readonly type: TaskRecordType;
  readonly id: string;
}

const RECORD_TYPES: readonly string[] = ["PROJECT", "CONTRACT", "ITP", "SCHEDULE", "FINDING"];

export function isTaskRecordType(value: string | null): value is TaskRecordType {
  return value !== null && RECORD_TYPES.includes(value);
}

export async function listTasks(
  token: string,
  options: {
    openOnly?: boolean | undefined;
    limit?: number | undefined;
    offset?: number | undefined;
    /** Wave W2: only the tasks about this record and the records under it. */
    record?: TaskRecord | null | undefined;
    signal?: AbortSignal | undefined;
  } = {},
): Promise<TaskList> {
  const queryString = query({
    open_only: options.openOnly,
    limit: options.limit,
    offset: options.offset,
    record_type: options.record?.type,
    record_id: options.record?.id,
  });
  return apiJson(token, `/tasks${queryString}`, parseTaskList, {
    signal: options.signal,
  });
}

export async function completeTask(token: string, taskId: string): Promise<TaskCompleted> {
  return apiJson(
    token,
    `/tasks/${taskId}/complete`,
    (raw) => {
      if (!isRecord(raw)) {
        throw new Error("Expected task completed record");
      }
      return {
        id: str(raw.id),
        completedAt: optStr(raw.completed_at),
      };
    },
    { method: "POST" },
  );
}

export function isTaskOpen(task: Task): boolean {
  return task.completedAt === null;
}

/** The scheduler marked this open task overdue (its mark, never the client's sum). */
export function isTaskOverdue(task: Pick<Task, "completedAt" | "overdueAt">): boolean {
  return task.completedAt === null && task.overdueAt !== null;
}
